/**
 * Life4Earth V2 — Onboarding API layer
 *
 * API functions for invitations, user archetypes, and milestones.
 * Follows the same pattern as projectRoles.ts and impactSignature.ts.
 *
 * Architecture ref: Onboarding Spec §4 (Invitation Flow),
 *                   §5 (Milestone Tracking)
 */

import { apiFetch, getCurrentUserGuid } from './client';
import { invalidateAndRefetch } from './queryCache';
import type { MilestoneKey, ArchetypeKey } from '../Data/Onboarding/archetypeConfig';

/**
 * Invalidate the current user's cached onboarding state across EVERY hook
 * instance (App.tsx, Action Center, LeftNav...).
 *
 * Why this exists (Skip bug report, 2026-07-08): the Explorer→Contributor
 * promotion is computed server-side on the next onboarding-state READ.
 * A hook instance that refetches privately (fetchForKey sets the cache
 * silently by design) learns about the promotion alone — App.tsx's
 * instance, which fires the Contributor welcome popup, stays stale until
 * reload. Every act that can complete a rung basic (follow, standards/
 * EULA accept, profile save, milestone write) must call this so the
 * promotion is observed live and the celebration fires at the moment it
 * happens. `invalidateAndRefetch` is the sanctioned notification path.
 */
export function invalidateOnboardingState(): void {
  const guid = getCurrentUserGuid();
  if (guid) invalidateAndRefetch(`onboarding:${guid}`);
}

// ── Response types ───────────────────────────────────────────

export interface InvitationResponse {
  invitationGuid: string;
  inviteCode: string;
  inviterUserGuid: string;
  inviterDisplayName: string;
  inviteeEmail: string | null;
  inviteeName: string | null;
  archetype: ArchetypeKey;
  communityGuid: string;
  communityName: string;
  /** Org's own logo — the welcome-panel avatar; null → initials fallback. */
  communityLogoUrl: string | null;
  /** Org's home-community hero photo for the accept-page welcome panel;
      null → brand-watermark fallback. */
  communityHeroUrl: string | null;
  personalMessage: string | null;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  expiresAt: string;
  createdAt: string;
}

export interface UserArchetypeResponse {
  userGuid: string;
  originalArchetype: ArchetypeKey;
  currentArchetype: ArchetypeKey;
  onboardingComplete: boolean;
  onboardedAt: string | null;
}

export interface UserMilestoneResponse {
  milestoneKey: MilestoneKey;
  archetype: ArchetypeKey;
  contextGuid: string | null;
  achievedAt: string;
}

export interface OnboardingStateResponse {
  archetype: UserArchetypeResponse | null;
  milestones: UserMilestoneResponse[];
  /** Admin manual overrides: {feature_key: true/false}. Applied on top of
      the archetype/milestone-computed feature set. */
  featureOverrides?: Record<string, boolean>;
  /** Profile provenance: self_selected | invited | inferred | admin_assigned.
      Chunk A (§7.9): the acclimation surfaces key off invited provenance. */
  source?: string | null;
  /** The inviting org community (resolved from the lineage edge). Targets the
      welcome nudge + "Meet your organization" CTA (§7.6 r3 / §7.7). */
  invitedCommunityGuid?: string | null;
}

// ── Request types ────────────────────────────────────────────

export interface CreateInvitationRequest {
  inviteeEmail?: string;
  inviteeName?: string;
  archetype: ArchetypeKey;
  communityGuid: string;
  personalMessage?: string;
}

export interface AcceptInvitationRequest {
  inviteCode: string;
  /** OAuth provider + token for account creation. */
  provider: 'google' | 'apple';
  idToken: string;
}

export interface RecordMilestoneRequest {
  milestoneKey: MilestoneKey;
  contextGuid?: string;
}

// ── Invitation endpoints ─────────────────────────────────────

/** Create a new invitation (requires invite_others feature). */
export async function createInvitation(
  req: CreateInvitationRequest
): Promise<InvitationResponse> {
  return apiFetch<InvitationResponse>('/api/invitations', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

/** Validate an invite code and get the invitation details (public). */
export async function validateInviteCode(
  code: string
): Promise<InvitationResponse> {
  return apiFetch<InvitationResponse>(`/api/invitations/validate/${encodeURIComponent(code)}`);
}

/** Accept an invitation and create an account. */
export async function acceptInvitation(
  req: AcceptInvitationRequest
): Promise<{ accessToken: string; refreshToken: string; user: unknown }> {
  return apiFetch('/api/invitations/accept', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

/** List invitations sent by the current user. */
export async function fetchMyInvitations(): Promise<InvitationResponse[]> {
  return apiFetch<InvitationResponse[]>('/api/invitations/mine');
}

/** Revoke a pending invitation. */
export async function revokeInvitation(invitationGuid: string): Promise<void> {
  await apiFetch(`/api/invitations/${invitationGuid}/revoke`, { method: 'POST' });
}

// ── Archetype & milestone endpoints ──────────────────────────

/** Get the current user's onboarding state (archetype + milestones). */
export async function fetchOnboardingState(
  userGuid: string
): Promise<OnboardingStateResponse> {
  return apiFetch<OnboardingStateResponse>(`/api/onboarding/${userGuid}/state`);
}

/** Record a milestone achievement for the current user. */
export async function recordMilestone(
  userGuid: string,
  req: RecordMilestoneRequest
): Promise<UserMilestoneResponse> {
  const res = await apiFetch<UserMilestoneResponse>(`/api/onboarding/${userGuid}/milestones`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
  // Notify ALL onboarding hook instances (not just the caller's) — a
  // milestone write can be the act that completes the rung basics, and
  // the promotion popup fires from App.tsx's instance.
  invalidateOnboardingState();
  return res;
}

/** Mark onboarding as complete for the current user. */
export async function completeOnboarding(
  userGuid: string
): Promise<UserArchetypeResponse> {
  return apiFetch<UserArchetypeResponse>(`/api/onboarding/${userGuid}/complete`, {
    method: 'POST',
  });
}
